import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Flame, Target, Trophy, ArrowRight, Wallet, Award, ChevronDown, ChevronUp, Activity,
} from "lucide-react";
import PipelineBoard from "@/components/PipelineBoard";
import EnterRoundDialog from "@/components/EnterRoundDialog";
import { SeedAvatar } from "@/components/SeedAvatar";
import {
  getActivityFeed,
  getLeaderboardV2,
  getMyApplications,
  getMyBadges,
  getMyRanks,
  getMyStats,
  listAvailableTasks,
} from "@/lib/afritask";

const fmtNaira = (n) => `₦${Number(n).toLocaleString("en-NG")}`;

export default function Dashboard() {
  const { me, wallet, refreshWallet } = useOutletContext();
  const [available, setAvailable] = useState([]);
  const [pipeline, setPipeline] = useState({
    in_progress: [], submitted: [], ranked: [], won: [],
  });
  const [stats, setStats] = useState({
    earned_this_week_ngn: 0, success_rate_pct: 0,
    tasks_completed_count: 0, current_streak_days: 0,
  });
  const [top, setTop] = useState([]);
  const [entering, setEntering] = useState(null);
  const [meStats, setMeStats] = useState(null);
  const [latestBadge, setLatestBadge] = useState(null);
  const [myRank, setMyRank] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);

  const load = async () => {
    const [av, mine, lb, s, bd, r] = await Promise.all([
      listAvailableTasks(5).catch(() => []),
      getMyApplications().catch(() => null),
      getLeaderboardV2("week", "earnings", 5).catch(() => ({ entries: [] })),
      getMyStats().catch(() => null),
      getMyBadges().catch(() => null),
      getMyRanks().catch(() => null),
    ]);
    setAvailable(av);
    if (mine) {
      setPipeline(mine.pipeline);
      setStats(mine.stats);
    }
    setTop(lb.entries || []);
    setMeStats(s);
    if (bd?.earned?.length) {
      const sorted = [...bd.earned].sort((a, b) => (a.earned_at < b.earned_at ? 1 : -1));
      setLatestBadge(sorted[0]);
    }
    setMyRank(r?.ranks?.week?.earnings || null);
  };

  const loadActivity = async () => {
    try {
      const f = await getActivityFeed(15);
      setActivity(f.events || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!me) return;
    load();
    loadActivity();
    // 15s poll for platform-wide activity
    const t = setInterval(loadActivity, 15_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.user_id]);

  const onEntered = async () => {
    await Promise.all([load(), refreshWallet(), loadActivity()]);
  };

  const hasSubmissions = !!stats.has_submissions;

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div
        className="rounded-2xl p-6 sm:p-8"
        style={{ background: "linear-gradient(135deg, #B45309 0%, #D97706 100%)" }}
      >
        <p className="text-sm text-amber-100">Welcome back,</p>
        <h2 className="mt-1 text-2xl font-bold text-white sm:text-3xl">
          {me?.display_name || me?.username || "warrior"}
        </h2>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-amber-100">
          <span className="inline-flex items-center gap-1.5" data-testid="welcome-streak">
            <Flame className="h-4 w-4" /> {meStats?.current_streak_days ?? 0}-day streak
          </span>
          <span className="opacity-40">•</span>
          <span>
            <span className="font-semibold text-white">
              {fmtNaira(stats.earned_this_week_ngn)}
            </span>{" "}
            earned this week
          </span>
          {latestBadge && (
            <>
              <span className="opacity-40">•</span>
              <Link
                to="/app/profile"
                className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 font-medium text-white transition hover:bg-white/25"
                data-testid="latest-badge-chip"
              >
                <Award className="h-3 w-3" /> Latest: {latestBadge.icon} {latestBadge.name}
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Stat cards — 3 across on mobile, tablet, and desktop */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <StatCard
          icon={Target} tone="amber" label="Success rate"
          value={hasSubmissions ? `${Math.round((meStats?.success_rate || 0) * 100) || stats.success_rate_pct}%` : "—"}
        />
        <StatCard
          icon={Flame} tone="rose" label="Streak"
          value={`${meStats?.current_streak_days ?? stats.current_streak_days ?? 0}d`}
        />
        <StatCard
          icon={Wallet} tone="amber" label="Work Points"
          value={wallet ? wallet.work_points.toLocaleString() : "—"}
          cta={
            <Link to="/app/wallet" className="text-[11px] font-medium text-amber-700 hover:text-amber-800">
              Top up →
            </Link>
          }
        />
      </div>

      {/* Pipeline */}
      <PipelineBoard
        available={available}
        pipeline={pipeline}
        onEnter={setEntering}
        completedCount={stats.tasks_completed_count}
        hideEmptyMiddleCols
      />

      {/* Recent activity — tiny, collapsible, platform-wide, moved below pipeline */}
      <Card data-testid="recent-activity-card" className="border-neutral-200">
        <button
          onClick={() => setActivityOpen((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-2.5 text-left"
          data-testid="recent-activity-toggle"
        >
          <div className="flex items-center gap-2">
            <Activity className="h-3.5 w-3.5 text-neutral-500" />
            <span className="text-xs font-semibold text-neutral-700">Recent activity</span>
            <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-neutral-500">
              {activity.length}
            </span>
            <span className="hidden text-[11px] text-neutral-400 sm:inline">· platform-wide · updates every 15s</span>
          </div>
          {activityOpen
            ? <ChevronUp className="h-4 w-4 text-neutral-400" />
            : <ChevronDown className="h-4 w-4 text-neutral-400" />}
        </button>
        {activityOpen && (
          <div className="border-t border-neutral-100">
            {activity.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-neutral-400">
                Nothing yet — someone enters, submits, or wins and it&apos;ll show up here.
              </p>
            ) : (
              <ul className="max-h-72 divide-y divide-neutral-100 overflow-y-auto" data-testid="recent-activity-list">
                {activity.map((a, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 px-4 py-2 text-xs">
                    <div className="flex items-start gap-2">
                      <span className="text-sm leading-none">{a.icon}</span>
                      <div className="min-w-0">
                        <p className="text-neutral-800">{a.text}</p>
                        {a.detail && <p className="mt-0.5 text-[11px] text-neutral-500">{a.detail}</p>}
                      </div>
                    </div>
                    <span className="shrink-0 text-[10px] tabular-nums text-neutral-400">{relTime(a.ts)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>

      {/* Your rank widget */}
      {myRank && (
        <Card
          className="border-amber-100 bg-gradient-to-r from-amber-50 to-white"
          data-testid="your-rank-widget"
        >
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-700 text-white">
                <Trophy className="h-4 w-4" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wider text-neutral-500">Your rank this week</p>
                <p className="text-sm font-semibold">
                  You&apos;re <span className="text-amber-700 text-lg">#{myRank}</span> in Top Earners
                </p>
              </div>
            </div>
            <Link to="/app/leaderboard">
              <Button variant="outline" size="sm" data-testid="rank-widget-cta">
                View leaderboard <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Top this week */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Top this week</CardTitle>
          <Trophy className="h-4 w-4 text-amber-500" />
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y divide-neutral-100">
            {top.slice(0, 5).map((l) => {
              const name = l.display_name || l.username;
              return (
                <li key={l.user_id || l.rank} className="flex items-center justify-between px-6 py-2.5 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-5 text-xs font-semibold tabular-nums text-neutral-500">#{l.rank}</span>
                    <SeedAvatar seed={name} size={22} />
                    <span className="truncate text-neutral-800">{name}</span>
                  </div>
                  <span className="text-xs font-semibold tabular-nums text-emerald-700">
                    {fmtNaira(l.value ?? l.lifetime_earned ?? 0)}
                  </span>
                </li>
              );
            })}
            {top.length === 0 && (
              <li className="px-6 py-6 text-center text-xs text-neutral-400">No leaderboard data yet.</li>
            )}
          </ul>
          <div className="border-t border-neutral-100 p-4">
            <Link to="/app/leaderboard">
              <Button variant="ghost" className="w-full justify-between">
                Full leaderboard <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <EnterRoundDialog task={entering} onClose={() => setEntering(null)} onEntered={onEntered} />
    </div>
  );
}

function StatCard({ icon: Icon, tone, label, value, cta }) {
  const tones = {
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-600",
    rose: "bg-rose-50 text-rose-600",
  };
  const accent =
    "rounded-xl border border-amber-300 bg-white shadow-sm ring-1 ring-amber-200/70 transition hover:border-amber-400 hover:ring-amber-300 hover:shadow-md";
  return (
    <Card className={accent}>
      {/* Mobile: moderate compact tile — small icon + label + value, ~89px tall */}
      <CardContent className="p-3 sm:hidden">
        <Icon className={`h-4 w-4 ${tones[tone]?.split(" ")[1] || "text-amber-700"}`} />
        <p className="mt-1.5 truncate text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          {label}
        </p>
        <p className="mt-0.5 truncate text-base font-semibold tabular-nums text-neutral-900">
          {value}
        </p>
      </CardContent>
      {/* Tablet + desktop: full card with tinted icon tile */}
      <CardContent className="hidden p-3 sm:block sm:p-3.5">
        <div className="flex items-center justify-between">
          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${tones[tone] || tones.amber}`}>
            <Icon className="h-3.5 w-3.5" />
          </span>
          {cta}
        </div>
        <p className="mt-2 text-[10px] font-medium uppercase tracking-wider text-neutral-500">
          {label}
        </p>
        <p className="mt-0.5 text-lg font-bold tabular-nums sm:text-xl">{value}</p>
      </CardContent>
    </Card>
  );
}

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
